import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { Course } from '@/client/courses';
import { LoadingStatuses } from '@/common/constants';
import { CourseCloneJob } from '@/components/courses/CourseCloneStatusStack';
import { useNotifications } from '@/hooks/notifications.hook';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';

export const useCourseClone = () => {
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);

  const [dialogCourse, setDialogCourse] = useState<Course | null>(null);
  const [cloningCourseIds, setCloningCourseIds] = useState<string[]>([]);
  const [cloneJobs, setCloneJobs] = useState<CourseCloneJob[]>([]);

  const jobIdBySourceCourseIdRef = useRef<Record<string, string>>({});
  const notifySourceCourseIdRef = useRef<string | undefined>(undefined);

  const {
    lastMessage: notifications,
    notificationParam,
    setNotificationParam,
    isConnected,
  } = useNotifications(client.courses.coursesNotifyUrl);

  useEffect(() => {
    notifySourceCourseIdRef.current = notificationParam;
  }, [notificationParam]);

  const removeCloneJob = useCallback(
    (jobId: string) =>
      setCloneJobs((prev) => prev.filter(({ id }) => id !== jobId)),
    [],
  );

  const handleCloneNotification = async (messages: { data: any } | null) => {
    if (
      !messages?.data?.event ||
      !['course-clone.progress', 'course-clone.completed'].includes(
        messages?.data?.event,
      )
    )
      return;

    if (messages.data.event === 'course-clone.progress') {
      const jobId = jobIdBySourceCourseIdRef.current[messages.data.courseId];
      if (!jobId) return;

      setCloneJobs((prev) =>
        prev.map((job) =>
          job.id === jobId ? { ...job, progress: messages.data.progress } : job,
        ),
      );
    }
  };

  useEffect(() => {
    if (!notifications) return;
    handleCloneNotification(notifications);
  }, [JSON.stringify(notifications)]);

  const openCloneDialog = (course: Course) => setDialogCourse(course);
  const closeCloneDialog = () => setDialogCourse(null);

  const submitClone = async (
    targetAccountId: string,
    targetAccountName?: string,
    course?: Course,
  ) => {
    const sourceCourse = course ?? dialogCourse;
    if (!sourceCourse) return;

    closeCloneDialog();

    const jobId = v4();
    const sourceCourseId = sourceCourse.id;
    const isOtherAccount = !!account?.id && targetAccountId !== account.id;

    setCloningCourseIds((prev) =>
      prev.includes(sourceCourseId) ? prev : [...prev, sourceCourseId],
    );

    setCloneJobs((prev) => [
      ...prev,
      { id: jobId, status: LoadingStatuses.LOADING, progress: 0 },
    ]);

    jobIdBySourceCourseIdRef.current[sourceCourseId] = jobId;
    setNotificationParam(sourceCourseId);

    // Wait for a notify connection to establish
    await new Promise((resolve) => {
      const intervalId = setInterval(() => {
        if (isConnected.current) {
          clearInterval(intervalId);
          resolve(true);
        }
      }, 100);
    });

    try {
      if (isConnected.current) {
        const { id: clonedCourseId } = await client.courses.cloneCourse(
          targetAccountId,
          sourceCourseId,
        );

        setCloneJobs((prev) =>
          prev.map((job) =>
            job.id === jobId
              ? {
                  ...job,
                  status: LoadingStatuses.IDLE,
                  result: {
                    clonedCourseId,
                    targetAccountId,
                    targetAccountName,
                    isOtherAccount,
                  },
                }
              : job,
          ),
        );
      }
    } catch (error) {
      setCloneJobs((prev) => prev.filter(({ id }) => id !== jobId));
      handleAxiosError(error as Error | AxiosError, toast);
    } finally {
      setCloningCourseIds((prev) => prev.filter((id) => id !== sourceCourseId));

      delete jobIdBySourceCourseIdRef.current[sourceCourseId];

      if (notifySourceCourseIdRef.current === sourceCourseId) {
        setNotificationParam(undefined);
      }
    }
  };

  return {
    dialogCourse,
    cloningCourseIds,
    cloneJobs,
    openCloneDialog,
    closeCloneDialog,
    submitClone,
    removeCloneJob,
  };
};
